import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API_BASE } from "@/lib/api";

/* Google's own "Sign in with Google" button.
 *
 * Rendered by Google's script rather than drawn here, because the ID token
 * comes back through their callback and their branding rules are a condition of
 * using the flow at all. What the app receives is a signed JWT that the server
 * verifies against Google's published keys — nothing is trusted client-side,
 * and nothing passes through a third party on the way.
 *
 * The client id comes from /capabilities rather than the build, so switching
 * Google sign-in on is a server restart rather than a frontend rebuild. Not
 * configured means this renders nothing: email and password is a complete way
 * to sign in, and an inert button that reports a server setting is worse than
 * no button.
 */
const SRC = "https://accounts.google.com/gsi/client";

function loadGis() {
  if (window.google?.accounts?.id) return Promise.resolve(true);
  const existing = document.querySelector(`script[src="${SRC}"]`);
  if (existing) {
    return new Promise((resolve) => {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => resolve(false));
    });
  }
  return new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(true);
    // Blocked script, no network, a privacy extension — none of which should
    // leave the sign-in screen looking broken.
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
  });
}

export default function GoogleButton({ onCredential, onError }) {
  const holder = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      let clientId = "";
      try {
        const r = await axios.get(`${API_BASE}/capabilities`);
        clientId = r.data?.google_sign_in?.client_id || "";
      } catch {
        return;   // the sign-in form itself still works; say nothing
      }
      if (!clientId || cancelled) return;
      if (!(await loadGis()) || cancelled || !holder.current) return;

      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async ({ credential }) => {
          try {
            await onCredential(credential);
          } catch (e) {
            onError?.(e?.response?.data?.detail || "Google sign-in failed. Please try again.");
          }
        },
      });
      window.google.accounts.id.renderButton(holder.current, {
        theme: "outline",
        size: "large",
        width: holder.current.offsetWidth || 320,
        text: "continue_with",
      });
      setReady(true);
    })();

    return () => { cancelled = true; };
  }, [onCredential, onError]);

  return (
    <>
      {ready && (
        <div className="my-6 flex items-center gap-3 text-[11px] uppercase tracking-widest text-ink-400">
          <div className="h-px bg-ivory-border flex-1" /> or <div className="h-px bg-ivory-border flex-1" />
        </div>
      )}
      <div ref={holder} className="flex justify-center" data-testid="login-google-btn" />
    </>
  );
}
